import { useState } from 'react'
import { APP_VERSION } from '../lib/version'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CircleDashed, HelpCircle, KeyRound, Lightbulb, ShieldAlert } from 'lucide-react'
import { API_ORIGIN, api } from '../lib/api'
import { atLeast, getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { CodeBlock, Panel } from '../components/primitives'
import { CompatibilityMatrix } from '../components/CompatibilityMatrix'
import { KeyModal } from '../components/KeyModal'
import { MyAgents } from '../components/MyAgents'

type ProviderChoice = 'claude-code' | 'cursor' | 'kiro' | 'opencode'

const PROVIDERS: Array<{ value: ProviderChoice; label: string; configPath: string }> = [
  { value: 'claude-code', label: 'Claude Code', configPath: '.mcp.json' },
  { value: 'cursor', label: 'Cursor', configPath: '.cursor/mcp.json' },
  { value: 'kiro', label: 'Kiro', configPath: '.kiro/settings/mcp.json' },
  { value: 'opencode', label: 'OpenCode', configPath: 'opencode.jsonc' },
]

const GUIDE_KEY = 'devpulse:connect-guide'

/** The optional Claude Code hooks: pinned instructions, prompts counted, a heartbeat while it thinks, remote control. */
const HOOKS = `{
  "hooks": {
    "SessionStart": [{ "hooks": [
      { "type": "command", "command": "node /path/to/devpulse/mcp/hooks/session-start.mjs", "timeout": 5 }
    ] }],
    "UserPromptSubmit": [{ "hooks": [
      { "type": "command", "command": "node /path/to/devpulse/mcp/hooks/prompt-submitted.mjs", "timeout": 5 },
      { "type": "command", "command": "node /path/to/devpulse/mcp/hooks/heartbeat.mjs start", "timeout": 5 }
    ] }],
    "Stop": [{ "hooks": [
      { "type": "command", "command": "node /path/to/devpulse/mcp/hooks/heartbeat.mjs stop", "timeout": 5 },
      { "type": "command", "command": "node /path/to/devpulse/mcp/hooks/remote-control.mjs", "timeout": 1860 }
    ] }]
  }
}`

/**
 * Connecting an agent — what everything else in DevPulse depends on. Written for somebody who has
 * never set an environment variable: a guide in plain words that folds away, the key generated in a
 * dialog without leaving the page, and the configuration with this deployment's own address in it.
 *
 * It is the "From your IDE" tab of Agentes › Conectar agente (pages/ConnectAgent.tsx), which already
 * names the page, so there it leaves out its own heading.
 */
export default function Onboarding({ embedded = false }: { embedded?: boolean }) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState<ProviderChoice>('claude-code')
  const [keyOpen, setKeyOpen] = useState(false)
  const canAdmin = atLeast(getSession()?.role, 'Admin')

  const { data: keys } = useQuery({ queryKey: ['me', 'credentials'], queryFn: api.me.credentials, retry: false, refetchInterval: 15_000 })
  const active = (keys ?? []).filter((k) => !k.revokedAt && (!k.expiresAt || new Date(k.expiresAt) > new Date()))
  const lastUsed = active
    .map((k) => k.lastUsedAt)
    .filter((when): when is string => Boolean(when))
    .sort()
    .at(-1)

  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return localStorage.getItem(GUIDE_KEY) !== 'closed'
    } catch {
      return true
    }
  })

  const chosen = PROVIDERS.find((p) => p.value === provider)!

  // The key is never in this snippet: the server reads it from the file save-key wrote, which only the person's
  // user opens (docs/refactor/57) — what makes the file safe to commit, and the key out of every program's environment.
  const env = {
    DEVPULSE_API_URL: API_ORIGIN,
    DEVPULSE_PROVIDER: provider,
  }

  /*
    Each tool writes the same server down differently, and a snippet that needs editing before it
    works is a snippet that does not work. OpenCode nests under `mcp`, takes the command as one
    array, and calls the environment `environment`; the others share Claude Code's shape.
  */
  // The server comes from vephus.ai, this version's own copy (scripts/publish-mcp.mjs; docs/refactor/55): npx installs
  // it with no npm account, and a versioned name is what makes npx fetch a new one when the snippet changes.
  const mcpArgs = ['-y', '--package', `https://vephus.ai/downloads/vephus-mcp-${APP_VERSION}.tgz`, 'devpulse-mcp']
  const mcpConfig = JSON.stringify(
    provider === 'opencode'
      ? {
          $schema: 'https://opencode.ai/config.json',
          mcp: {
            devpulse: {
              type: 'local',
              command: ['npx', ...mcpArgs],
              enabled: true,
              environment: env,
            },
          },
        }
      : {
          mcpServers: {
            devpulse: {
              command: 'npx',
              args: mcpArgs,
              env,
            },
          },
        },
    null,
    2,
  )

  return (
    <div className="stack stack--loose connect" style={{ maxWidth: 860 }}>
      <div className="row row--wrap connect__head">
        <div className="stack stack--tight" style={{ flex: '1 1 320px' }}>
          {!embedded && <h2>{t('onboarding.title')}</h2>}
          <p className="secondary">{t('connect.subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setKeyOpen(true)}>
          <KeyRound size={15} aria-hidden /> {t('connect.key.open')}
        </button>
      </div>

      <div className={`connect__status small${lastUsed ? ' connect__status--ok' : ''}`} role="status">
        {lastUsed ? <Check size={15} aria-hidden /> : <CircleDashed size={15} aria-hidden />}
        {lastUsed
          ? t('connect.status.connected', { when: formatRelative(lastUsed, locale) })
          : active.length > 0
            ? t('connect.status.keyNotUsed')
            : t('connect.status.noKey')}
      </div>

      <details
        className="panel connect__guide"
        open={guideOpen}
        onToggle={(event) => {
          const open = (event.currentTarget as HTMLDetailsElement).open
          setGuideOpen(open)
          try {
            localStorage.setItem(GUIDE_KEY, open ? 'open' : 'closed')
          } catch {
            // Remembering is a nicety.
          }
        }}
      >
        <summary className="panel__header">
          <HelpCircle size={16} aria-hidden />
          <h2 className="panel__title">{t('connect.guide.title')}</h2>
        </summary>
        <div className="panel__body stack">
          <p className="small">{t('connect.guide.what')}</p>
          <ol className="connect__simple small">
            <li>
              <strong>{t('connect.guide.s1.title')}</strong> {t('connect.guide.s1.body')}
            </li>
            <li>
              <strong>{t('connect.guide.s2.title')}</strong> {t('connect.guide.s2.body')}
            </li>
            <li>
              <strong>{t('connect.guide.s3.title')}</strong> {t('connect.guide.s3.body')}
            </li>
            <li>
              <strong>{t('connect.guide.s4.title')}</strong> {t('connect.guide.s4.body')}
            </li>
          </ol>
          <div className="stack stack--tight">
            <span className="field__label">{t('connect.guide.faq')}</span>
            {['safe', 'once', 'nothing', 'tools'].map((question) => (
              <div key={question} className="connect__faq">
                <span className="small bold">{t(`connect.faq.${question}.q`)}</span>
                <span className="small secondary">{t(`connect.faq.${question}.a`)}</span>
              </div>
            ))}
          </div>
        </div>
      </details>

      <Panel title={`1. ${t('onboarding.step2')}`}>
        <div className="stack stack--tight">
          <div className="row row--tight row--wrap">
            {PROVIDERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`btn${provider === option.value ? ' btn--primary' : ''}`}
                onClick={() => setProvider(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="tiny muted">{t('connect.otherTools')}</span>
        </div>
      </Panel>

      <Panel title={`2. ${t('connect.step.key')}`}>
        <div className="stack">
          <p className="small secondary">{t('connect.step.keyHint')}</p>
          <button type="button" className="btn btn--primary" onClick={() => setKeyOpen(true)} style={{ alignSelf: 'flex-start' }}>
            <KeyRound size={14} aria-hidden /> {t('connect.key.open')}
          </button>
          {active.length > 0 && (
            <ul className="connect__keys">
              {active.map((k) => (
                <li key={k.id} className="row row--tight small">
                  <KeyRound size={13} aria-hidden className="muted" />
                  <span className="mono">{k.keyPrefix}</span>
                  <span className="truncate">{k.machineLabel ?? k.name}</span>
                  <div className="spacer" />
                  <span className="tiny muted">
                    {k.lastUsedAt ? t('remote.keys.used', { when: formatRelative(k.lastUsedAt, locale) }) : t('remote.keys.neverUsed')}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {canAdmin && <span className="tiny muted">{t('connect.step.keyAdmin')}</span>}
        </div>
      </Panel>

      <Panel title={`3. ${t('onboarding.step4')}`}>
        <div className="stack">
          <p className="small secondary">{t('connect.step.configHint', { file: chosen.configPath })}</p>
          <p className="tiny muted mono">{chosen.configPath}</p>
          <CodeBlock code={mcpConfig} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          <div className="connect__warning small">
            <ShieldAlert size={14} aria-hidden /> {t('connect.step.noKeyInFile')}
          </div>
          {/* For whoever is lost at this step: their own AI knows its tool better than any page. */}
          <div className="connect__tip small">
            <Lightbulb size={14} aria-hidden /> {t('connect.step.askYourAi')}
          </div>
        </div>
      </Panel>

      <Panel title={`4. ${t('onboarding.instructionsTitle')}`}>
        <div className="stack">
          <p className="small secondary">{t('onboarding.instructionsHint')}</p>
          <CodeBlock code={INSTRUCTIONS} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
        </div>
      </Panel>

      <Panel title={`5. ${t('connect.step.test')}`}>
        <div className="stack stack--tight">
          <p className="small">{t('connect.step.testHow')}</p>
          <CodeBlock code={t('connect.step.testPrompt')} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          {lastUsed ? (
            <div className="row row--tight small" style={{ color: 'var(--ok-text)' }}>
              <Check size={16} aria-hidden />
              <span>{t('connect.status.connected', { when: formatRelative(lastUsed, locale) })}</span>
            </div>
          ) : (
            <p className="small secondary">{t('connect.step.testWaiting')}</p>
          )}
        </div>
      </Panel>

      {provider === 'claude-code' && (
        <details className="panel connect__advanced">
          <summary className="panel__header">
            <h2 className="panel__title">{t('connect.hooks.title')}</h2>
          </summary>
          <div className="panel__body stack">
            <p className="small secondary">{t('connect.hooks.hint')}</p>
            <CodeBlock code={HOOKS} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          </div>
        </details>
      )}

      {/* Several agents at once, each a character of its own in the room. */}
      <Panel title={t('agents.title')}>
        <MyAgents />
      </Panel>

      <Panel title={t('compat.title')}>
        <CompatibilityMatrix />
      </Panel>

      {keyOpen && (
        <KeyModal
          onClose={() => setKeyOpen(false)}
          onIssued={() => void queryClient.invalidateQueries({ queryKey: ['me', 'credentials'] })}
        />
      )}
    </div>
  )
}

/**
 * The instructions a team pastes into their agent configuration.
 *
 * Deliberately short. A model reads this alongside everything else in its context, and a long
 * telemetry policy competes with the actual task. What it has to convey is: report meaningful
 * transitions, not every keystroke; never send content; and do not tell the user the work is done
 * because complete_work returned.
 */
const INSTRUCTIONS = `## Vephus telemetry

When you begin a substantial piece of work (a bug fix, a feature, a refactor):

1. Call \`devpulse_start_work\` with the task id, repository, branch and model.
2. Call \`devpulse_create_plan\` with weighted steps BEFORE making substantial changes.
   Vephus computes progress from these weights — it will ignore any percentage you
   claim for the task as a whole.
3. Report transitions as they happen:
   - \`devpulse_start_step\` / \`devpulse_complete_step\` around each step
   - \`devpulse_update_step\` at meaningful milestones only
   - \`devpulse_report_file_change\` after a batch of edits, not after every write
   - \`devpulse_report_test_run\` when tests start and finish
   - \`devpulse_report_blocker\` as soon as something stops you
   - \`devpulse_report_document_update\` when you touch SPEC.md, DESIGN.md and similar
   - \`devpulse_report_git_activity\` for commits, pushes and pull requests
   - \`devpulse_report_ai_usage\` when your environment exposes token counts
4. Call \`devpulse_complete_work\` when the implementation is finished.

Do NOT send: source code, prompts, reasoning, diffs, file contents, terminal output,
environment variable values, passwords or any secret. Metadata only.

Do NOT report telemetry for trivial operations: a single file read, a question, a
one-line edit.

\`devpulse_complete_work\` means "I have finished implementing". It usually does NOT
close the task — most organizations require human review or a merged pull request.
Do not tell the user the work is done on the strength of that call alone.`
